import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { fetchBranding } from "@/lib/branding";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Package, ShoppingCart, Receipt, Users2, CalendarClock,
  FileText, BarChart3, Wallet, ClipboardList, Menu, LogOut, Zap, Percent, X, Truck,
  Activity, MessageSquare, TrendingUp, ClipboardCheck, ScrollText, BadgePercent,
  ReceiptText, Trash2, FileCheck2, Cable,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import PublishSyncBanner from "@/components/PublishSyncBanner";
import ActivityPopup from "@/components/ActivityPopup";
import { ConfirmHost } from "@/components/ConfirmDialog";
import GlobalScanner from "@/components/GlobalScanner";

const adminNav = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, end: true },
  { to: "/stok", label: "Stok Yönetimi", icon: Package },
  { to: "/hizli-satis", label: "Hızlı Satış", icon: Zap },
  { to: "/talepler", label: "Sipariş Talepleri", icon: ClipboardList },
  { to: "/alis", label: "Alış Faturaları", icon: ShoppingCart },
  { to: "/satis", label: "Satış Faturaları", icon: Receipt },
  { to: "/efatura", label: "Fatura Kes (e-Fatura / e-Arşiv)", icon: FileCheck2 },
  { to: "/irsaliyeler", label: "e-İrsaliye", icon: Truck },
  { to: "/cari", label: "Cari Hesaplar", icon: Wallet },
  { to: "/makbuz", label: "Tahsilat Makbuzu", icon: ReceiptText },
  { to: "/evraklar", label: "Vadesi Gelmemiş Evraklar", icon: ScrollText },
  { to: "/teklifler", label: "Teklifler", icon: FileText },
  { to: "/iskontolar", label: "İskontolar", icon: BadgePercent },
  { to: "/kablo-bulten", label: "Kablo Bülten", icon: Cable },
  { to: "/hazirlik", label: "Hazırlanacak Listeler", icon: ClipboardCheck },
  { to: "/sohbet", label: "Sohbet", icon: MessageSquare },
  { to: "/aktiviteler", label: "Aktiviteler", icon: Activity },
  { to: "/performans", label: "Performans", icon: TrendingUp },
  { to: "/ajanda", label: "Ajanda", icon: CalendarClock },
  { to: "/kdv", label: "KDV & Maliye", icon: Percent },
  { to: "/raporlar", label: "Raporlar", icon: BarChart3 },
  { to: "/kullanicilar", label: "Kullanıcılar", icon: Users2 },
  { to: "/cop-kutusu", label: "Çöp Kutusu", icon: Trash2 },
];

const workerNav = [
  { to: "/", label: "Ürünler & Sayım", icon: Package, end: true },
  { to: "/hizli-satis", label: "Hızlı Satış", icon: Zap },
  { to: "/talepler", label: "Sipariş Talebi", icon: ClipboardList },
  { to: "/hazirlik", label: "Hazırlanacak Listeler", icon: ClipboardCheck },
  { to: "/sohbet", label: "Sohbet", icon: MessageSquare },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [unreadChat, setUnreadChat] = useState(0);
  const [incomingNew, setIncomingNew] = useState(0);
  const location = useLocation();
  const nav = user?.role === "admin" ? adminNav : workerNav;

  useEffect(() => { if (user) fetchBranding(); }, [user]);

  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        if (user?.role === "admin") {
          const { data } = await api.get("/chat/channels");
          const cnt = (data || []).reduce((s, c) => s + (c.unread || 0), 0);
          if (!stop) setUnreadChat(cnt);
          try { const { data: st } = await api.get("/qnb/status"); if (!stop) setIncomingNew(st.incoming_new || 0); } catch { /* noop */ }
        } else {
          const { data } = await api.get("/chat/messages");
          const seen = Number(localStorage.getItem("chat_seen") || 0);
          const cnt = (data || []).filter(
            (m) => m.sender_role !== user?.role && new Date(m.created_at).getTime() > seen
          ).length;
          if (!stop) setUnreadChat(cnt);
        }
      } catch { /* noop */ }
    };
    if (!user) return;
    poll();
    const iv = setInterval(poll, 12000);
    return () => { stop = true; clearInterval(iv); };
  }, [user]);

  useEffect(() => {
    if (location.pathname === "/sohbet") {
      localStorage.setItem("chat_seen", String(Date.now()));
      setUnreadChat(0);
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-background">
      {user?.role === "admin" && <ActivityPopup />}
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-72 bg-[#0b0f19] text-white flex flex-col transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="app-sidebar"
      >
        <div className="h-20 flex items-center gap-3 px-6 border-b border-white/10">
          <div className="h-11 w-11 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <Zap className="h-6 w-6 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-head font-black text-lg tracking-tight">GÜMÜŞALAN</div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-primary font-bold">ELEKTRİK</div>
          </div>
          <button className="ml-auto lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.to === "/" ? "home" : n.to.slice(1)}`}
              className={({ isActive }) =>
                cn(
                  "nav-link flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium border border-transparent",
                  isActive
                    ? "bg-primary text-white shadow-lg shadow-primary/20"
                    : n.to === "/hizli-satis"
                      ? "text-red-500 font-extrabold hover:text-red-400 hover:bg-red-500/10"
                      : "text-white/60 hover:text-white hover:bg-white/5"
                )
              }
            >
              <n.icon className="h-[18px] w-[18px]" />
              <span className="flex-1">{n.label}</span>
              {n.to === "/sohbet" && unreadChat > 0 && (
                <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center animate-pulse" data-testid="chat-unread-badge">
                  {unreadChat > 9 ? "9+" : unreadChat}
                </span>
              )}
              {n.to === "/alis" && incomingNew > 0 && (
                <span className="ml-auto h-5 min-w-5 px-1.5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center" title="Yeni gelen e-Fatura" data-testid="incoming-new-badge">
                  {incomingNew > 9 ? "9+" : incomingNew}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="h-9 w-9 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center text-sm font-bold text-primary">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-white/50 uppercase tracking-wider">
                {user?.role === "admin" ? "Yönetici" : "Depo Müdürü"}
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/5"
            onClick={logout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-card border-b flex items-center gap-4 px-4 lg:px-8 sticky top-0 z-30">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
            <Menu className="h-6 w-6" />
          </button>
          <div className="text-sm text-muted-foreground hidden sm:block">
            {new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <div className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-[#16A34A] animate-pulse" /> Çevrimiçi
            </div>
          </div>
        </header>
        {user?.role === "admin" && <PublishSyncBanner />}
        <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">{children}</main>
      </div>
      <ConfirmHost />
      <GlobalScanner />
    </div>
  );
}
